import { useEffect, useRef, useCallback } from "react";
import { toast } from "sonner";

/**
 * useIdleLogout
 * ─────────────
 * Auto-logout after `idleMinutes` of no user activity. Emits a warning toast
 * 30s before the logout fires so admins never lose work silently.
 *
 * Activity signals: pointer moves, key presses, scroll, touch, visibilitychange
 * (regaining focus). Timer resets on every event via debounce.
 *
 * Persists a per-tab "last-activity" timestamp in localStorage so multiple
 * open tabs share the same idle countdown — active work in ANY tab defers
 * the logout everywhere.
 *
 * Params:
 *   enabled       — pass falsy when no session is active (avoids stale timers)
 *   idleMinutes   — default 5
 *   onLogout      — callback invoked once when the timer expires
 *   storageKey    — namespaced so admin vs employer sessions don't collide
 */
export default function useIdleLogout({ enabled, idleMinutes = 5, onLogout, storageKey = "adelina_last_activity" }) {
  const timerRef = useRef(null);
  const warningRef = useRef(null);

  const clearTimers = useCallback(() => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    if (warningRef.current) { clearTimeout(warningRef.current); warningRef.current = null; }
  }, []);

  const scheduleLogout = useCallback(() => {
    clearTimers();
    if (!enabled) return;
    const ms = idleMinutes * 60 * 1000;
    // Warn 30s before fire so the user has a chance to move the mouse.
    warningRef.current = setTimeout(() => {
      toast.warning(`You'll be logged out in 30 seconds due to inactivity`, {
        id: "idle-warning",
        duration: 25_000,
      });
    }, Math.max(1000, ms - 30_000));
    timerRef.current = setTimeout(() => {
      toast.dismiss("idle-warning");
      toast.info("Logged out — session inactive for 5 minutes");
      onLogout?.();
    }, ms);
  }, [enabled, idleMinutes, onLogout, clearTimers]);

  const kick = useCallback(() => {
    try { localStorage.setItem(storageKey, String(Date.now())); } catch { /* ignore */ }
    scheduleLogout();
  }, [scheduleLogout, storageKey]);

  useEffect(() => {
    if (!enabled) { clearTimers(); return; }
    const events = ["mousemove", "mousedown", "keydown", "touchstart", "scroll", "wheel"];
    const onActivity = () => kick();
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    const onVis = () => { if (document.visibilityState === "visible") kick(); };
    document.addEventListener("visibilitychange", onVis);

    // Cross-tab sync: another tab's activity resets our countdown too.
    const onStorage = (e) => { if (e.key === storageKey) scheduleLogout(); };
    window.addEventListener("storage", onStorage);

    // Prime the timer on mount.
    kick();

    return () => {
      events.forEach((e) => window.removeEventListener(e, onActivity));
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("storage", onStorage);
      clearTimers();
    };
  }, [enabled, kick, scheduleLogout, clearTimers, storageKey]);
}
